import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Avatar, Button, Card, Collapse, Divider, Form, Input, InputNumber,
  Modal, Popconfirm, Select, Space, Table, Tag, Tooltip, Typography, message,
} from 'antd';
import {
  ApiOutlined, DisconnectOutlined, PlusOutlined, ReloadOutlined, StopOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import apiClient from '../../utils/axios';

dayjs.extend(relativeTime);

const { Title, Text, Paragraph } = Typography;
const { Panel } = Collapse;

const SCOPES = [
  'mcp:read', 
  //'mcp:config:write', 
  //'mcp:integrations:read'
];

const TOUCHFLOW_LOCAL_TOOLS = [
  //{ title: 'Contacts (mcp:read)', tools: ['contacts_get', 'contacts_search', 'contacts_list', 'contacts_property_history', 'contacts_latest_with_insights'] },
  //{ title: 'Timeline (mcp:read)', tools: ['timeline_get', 'timeline_events'] },
  { title: 'Intelligence (mcp:read)', tools: ['intelligence_latest', 'intelligence_detail'] },
  //{ title: 'Lifecycles', tools: ['lifecycles_list', 'lifecycles_get', 'lifecycles_funnel', 'lifecycles_for_contact'] },
  //{ title: 'Lifecycles — writes (needs mcp:config:write)', tools: ['lifecycles_create', 'lifecycles_update', 'lifecycles_delete'] },
  //{ title: 'Canonical properties', tools: ['canonical_properties_list', 'canonical_properties_get', 'canonical_properties_all_integration_fields', 'canonical_properties_tracked_keys'] },
  //{ title: 'Canonical properties — writes (needs mcp:config:write)', tools: ['canonical_properties_create', 'canonical_properties_update', 'canonical_properties_delete', 'canonical_properties_link', 'canonical_properties_unlink', 'canonical_properties_suggest_links', 'canonical_properties_apply_link_suggestions'] },
  //{ title: 'Integrations (needs mcp:integrations:read)', tools: ['integrations_list', 'integrations_get', 'integrations_types', 'integrations_statistics'] },
];

const RelativeTime = ({ value }) => {
  if (!value) return <Text type="secondary">—</Text>;
  return (
    <Tooltip title={dayjs(value).format('YYYY-MM-DD HH:mm:ss UTC')}>
      <span>{dayjs(value).fromNow()}</span>
    </Tooltip>
  );
};

const AppAvatar = ({ logoUri }) => {
  const [imgError, setImgError] = useState(false);
  if (logoUri && !imgError) {
    return (
      <Avatar
        size="small"
        src={logoUri}
        onError={() => { setImgError(true); return true; }}
      />
    );
  }
  return <Avatar size="small" icon={<ApiOutlined />} />;
};

const McpTokensPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);

  // OAuth sections
  const [connectedApps, setConnectedApps] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [appsLoading, setAppsLoading] = useState(false);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [filterClientId, setFilterClientId] = useState(null);

  // Manual tokens
  const [manualTokens, setManualTokens] = useState([]);
  const [manualLoading, setManualLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newToken, setNewToken] = useState(null);
  const [form] = Form.useForm();

  const checkAuth = useCallback(async () => {
    try {
      const res = await apiClient.get('/iam/me');
      if (res.data.role !== 'admin') {
        message.error('Tenant admin access required.');
        setTimeout(() => navigate('/dashboard'), 2500);
        return false;
      }
      setUser(res.data);
      return true;
    } catch {
      navigate('/login');
      return false;
    }
  }, [navigate]);

  const loadOAuth = useCallback(async () => {
    setAppsLoading(true);
    setSessionsLoading(true);
    await Promise.allSettled([
      apiClient.get('/iam/oauth/connected-apps')
        .then(r => setConnectedApps(r.data || []))
        .catch(() => message.error('Failed to load connected apps'))
        .finally(() => setAppsLoading(false)),
      apiClient.get('/iam/oauth/sessions')
        .then(r => setSessions(r.data || []))
        .catch(() => message.error('Failed to load sessions'))
        .finally(() => setSessionsLoading(false)),
    ]);
  }, []);

  const loadManual = useCallback(async () => {
    setManualLoading(true);
    try {
      const res = await apiClient.get('/iam/mcp-tokens');
      setManualTokens(res.data || []);
    } catch {
      message.error('Failed to load manual tokens');
    } finally {
      setManualLoading(false);
    }
  }, []);

  const loadAll = useCallback(async () => {
    await Promise.allSettled([loadOAuth(), loadManual()]);
  }, [loadOAuth, loadManual]);

  useEffect(() => {
    const init = async () => {
      setPageLoading(true);
      const ok = await checkAuth();
      if (ok) await loadAll();
      setPageLoading(false);
    };
    init();
  }, [checkAuth, loadAll]);

  const handleDisconnectApp = async (clientId, clientName, sessionCount) => {
    try {
      const res = await apiClient.post(`/iam/oauth/connected-apps/${encodeURIComponent(clientId)}/revoke`);
      message.success(`Disconnected ${clientName || clientId}: ${res.data?.revoked_count ?? sessionCount} session(s) revoked`);
      if (filterClientId === clientId) setFilterClientId(null);
      await loadOAuth();
    } catch (e) {
      message.error(e.response?.data?.detail || 'Failed to disconnect app');
    }
  };

  const handleRevokeSession = async (sessionId) => {
    try {
      await apiClient.post(`/iam/oauth/sessions/${sessionId}/revoke`);
      message.success('Session revoked');
      await loadOAuth();
    } catch (e) {
      message.error(e.response?.data?.detail || 'Failed to revoke session');
    }
  };

  const handleCreateManual = async (values) => {
    setCreating(true);
    try {
      const body = { name: values.name.trim(), scopes: values.scopes };
      if (values.expires_in_days) body.expires_in_days = values.expires_in_days;
      const res = await apiClient.post('/iam/mcp-tokens', body);
      setNewToken(res.data.token);
      setCreateOpen(false);
      form.resetFields();
      message.success('Token created — copy it now');
      await loadManual();
    } catch (e) {
      message.error(e.response?.data?.detail || 'Failed to create token');
    } finally {
      setCreating(false);
    }
  };

  const handleRevokeManual = async (id) => {
    try {
      await apiClient.post(`/iam/mcp-tokens/${id}/revoke`);
      message.success('Token revoked');
      await loadManual();
    } catch (e) {
      message.error(e.response?.data?.detail || 'Failed to revoke token');
    }
  };

  const handleDeleteManual = async (id) => {
    try {
      await apiClient.delete(`/iam/mcp-tokens/${id}`);
      message.success('Token deleted');
      await loadManual();
    } catch (e) {
      message.error(e.response?.data?.detail || 'Failed to delete token');
    }
  };

  if (pageLoading) return <div style={{ padding: '2rem' }}>Loading MCP access…</div>;
  if (!user) return null;

  const filteredSessions = filterClientId
    ? sessions.filter(s => s.client_id === filterClientId)
    : sessions;

  // ── Connected Apps columns ──────────────────────────────────────────────
  const appColumns = [
    {
      title: 'App',
      key: 'app',
      render: (_, r) => (
        <Space>
          <AppAvatar logoUri={r.logo_uri} />
          <Text strong>{r.client_name || r.client_id}</Text>
        </Space>
      ),
    },
    {
      title: 'Sessions',
      dataIndex: 'session_count',
      key: 'sessions',
      render: (count, r) => (
        <Button
          type="link"
          size="small"
          style={{ padding: 0 }}
          onClick={() => setFilterClientId(filterClientId === r.client_id ? null : r.client_id)}
        >
          {count}
        </Button>
      ),
    },
    {
      title: 'Users',
      dataIndex: 'distinct_user_count',
      key: 'users',
    },
    {
      title: 'Scopes',
      dataIndex: 'scopes_in_use',
      key: 'scopes',
      render: (scopes) => (
        <Space wrap size={[4, 4]}>
          {(scopes || []).map(s => <Tag key={s}>{s}</Tag>)}
        </Space>
      ),
    },
    {
      title: 'Last used',
      dataIndex: 'last_used_at',
      key: 'last_used',
      render: (v) => <RelativeTime value={v} />,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, r) => (
        <Popconfirm
          title={`Disconnect ${r.client_name || r.client_id}?`}
          description={`This will revoke all ${r.session_count} session(s) immediately.`}
          onConfirm={() => handleDisconnectApp(r.client_id, r.client_name, r.session_count)}
          okText="Disconnect"
          okButtonProps={{ danger: true }}
        >
          <Tooltip title="Disconnect all sessions">
            <Button icon={<DisconnectOutlined />} size="small" danger />
          </Tooltip>
        </Popconfirm>
      ),
    },
  ];

  // ── Sessions columns ────────────────────────────────────────────────────
  const sessionColumns = [
    {
      title: 'App',
      key: 'app',
      render: (_, r) => (
        <Space>
          <AppAvatar logoUri={r.logo_uri} />
          <Text>{r.client_name || r.client_id}</Text>
        </Space>
      ),
    },
    {
      title: 'User',
      key: 'user',
      render: (_, r) => <Text>{r.user_email || r.user_id}</Text>,
    },
    {
      title: 'Scopes',
      dataIndex: 'scopes',
      key: 'scopes',
      render: (scopes) => (
        <Space wrap size={[4, 4]}>
          {(scopes || []).map(s => <Tag key={s}>{s}</Tag>)}
        </Space>
      ),
    },
    {
      title: 'Issued',
      dataIndex: 'created_at',
      key: 'issued',
      render: (v) => <RelativeTime value={v} />,
    },
    {
      title: 'Refresh expires',
      dataIndex: 'expires_at',
      key: 'expires',
      render: (v) => <RelativeTime value={v} />,
    },
    {
      title: 'Last used',
      dataIndex: 'last_used_at',
      key: 'last_used',
      render: (v) => <RelativeTime value={v} />,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, r) => (
        <Popconfirm
          title="Revoke this session?"
          description="The access token is invalidated immediately."
          onConfirm={() => handleRevokeSession(r.id)}
          okText="Revoke"
          okButtonProps={{ danger: true }}
        >
          <Tooltip title="Revoke session">
            <Button icon={<StopOutlined />} size="small" danger />
          </Tooltip>
        </Popconfirm>
      ),
    },
  ];

  // ── Manual tokens columns ───────────────────────────────────────────────
  const manualColumns = [
    { title: 'Name', dataIndex: 'name', key: 'name', render: (v) => <Text strong>{v}</Text> },
    {
      title: 'Prefix',
      dataIndex: 'token_prefix',
      key: 'prefix',
      render: (v) => <Text code>{v}…</Text>,
    },
    {
      title: 'Scopes',
      dataIndex: 'scopes',
      key: 'scopes',
      render: (scopes) => (
        <Space wrap size={[4, 4]}>
          {(scopes || []).map(s => <Tag key={s}>{s}</Tag>)}
        </Space>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created',
      render: (v) => <RelativeTime value={v} />,
    },
    {
      title: 'Expires',
      dataIndex: 'expires_at',
      key: 'expires',
      render: (v) => v ? <RelativeTime value={v} /> : <Text type="secondary">∞</Text>,
    },
    {
      title: 'Last used',
      dataIndex: 'last_used_at',
      key: 'last_used',
      render: (v) => <RelativeTime value={v} />,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, r) => (
        <Space size="small">
          <Popconfirm
            title="Revoke this token?"
            description="It will stop working immediately."
            onConfirm={() => handleRevokeManual(r.id)}
            okText="Revoke"
            okButtonProps={{ danger: true }}
          >
            <Button size="small">Revoke</Button>
          </Popconfirm>
          <Popconfirm
            title="Permanently delete this token record?"
            onConfirm={() => handleDeleteManual(r.id)}
            okText="Delete"
            okButtonProps={{ danger: true }}
          >
            <Button size="small" danger type="text">Delete</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const anyLoading = appsLoading || sessionsLoading || manualLoading;

  return (
    <div>
      {/* Page header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>MCP Access</Title>
          <Text type="secondary">Manage AI assistant connections and API tokens for the TouchFlow MCP server.</Text>
        </div>
        <Button icon={<ReloadOutlined />} onClick={loadAll} loading={anyLoading}>Refresh</Button>
      </div>

      {/* Connected Apps */}
      <Card title="Connected Apps" style={{ marginBottom: 16 }}>
        <Table
          rowKey="client_id"
          columns={appColumns}
          dataSource={connectedApps}
          loading={appsLoading}
          pagination={false}
          locale={{ emptyText: 'No OAuth-connected apps. Connect Cursor or Claude Desktop to see them here.' }}
          size="small"
        />
      </Card>

      {/* Sessions */}
      <Card
        title="Sessions"
        style={{ marginBottom: 16 }}
        extra={
          <Space>
            {filterClientId && (
              <Button size="small" onClick={() => setFilterClientId(null)}>Clear filter</Button>
            )}
            <Text type="secondary">{filteredSessions.length} active</Text>
          </Space>
        }
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="Sessions auto-rotate every hour — the 30-day refresh window is what keeps a connection alive. Revoking a session immediately disables the access token."
        />
        <Table
          rowKey="id"
          columns={sessionColumns}
          dataSource={filteredSessions}
          loading={sessionsLoading}
          pagination={false}
          locale={{ emptyText: 'No active sessions.' }}
          size="small"
        />
      </Card>

      {/* Advanced — Manual tokens */}
      <Collapse defaultActiveKey={[]}>
        <Panel header="Advanced: Manual API Tokens (tfmcp_…)" key="manual">
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message="For non-interactive automation only"
            description="OAuth (Connected Apps above) is the recommended path for Cursor and Claude Desktop. Use manual tokens only for headless agents and CI pipelines."
          />

          {newToken && (
            <Alert
              type="success"
              showIcon
              closable
              style={{ marginBottom: 12 }}
              onClose={() => setNewToken(null)}
              message="Token created — copy it now, it won't be shown again"
              description={
                <Paragraph
                  copyable={{ text: newToken }}
                  style={{ marginBottom: 0, fontFamily: 'monospace', wordBreak: 'break-all' }}
                >
                  {newToken}
                </Paragraph>
              }
            />
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <Text strong>Tokens ({manualTokens.length})</Text>
            <Button type="primary" icon={<PlusOutlined />} size="small" onClick={() => setCreateOpen(true)}>
              New token
            </Button>
          </div>

          <Table
            rowKey="id"
            columns={manualColumns}
            dataSource={manualTokens}
            loading={manualLoading}
            pagination={false}
            locale={{ emptyText: 'No manual tokens. Create one for headless automation.' }}
            size="small"
          />

          <Divider />

          <Collapse ghost>
            <Panel header="MCP server endpoint" key="endpoint">
              <Paragraph copyable code style={{ marginBottom: 8 }}>
                {process.env.REACT_APP_API_URL}/mcp
              </Paragraph>
              <Text type="secondary">
                Pass the token as <Text code>Authorization: Bearer &lt;token&gt;</Text>.
                Tokens are shown only once at creation — store them securely.
              </Text>
            </Panel>
            <Panel header="Cursor: touchflow-local — tool names" key="tools">
              <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                Use these exact <Text code>name</Text> strings (underscore form). Baseline scope is <Text code>mcp:read</Text>.
              </Text>
              {TOUCHFLOW_LOCAL_TOOLS.map(({ title, tools }) => (
                <Card size="small" title={title} style={{ marginBottom: 8 }} key={title}>
                  <Space wrap size={[4, 4]}>
                    {tools.map(t => <Tag key={t}>{t}</Tag>)}
                  </Space>
                </Card>
              ))}
            </Panel>
          </Collapse>
        </Panel>
      </Collapse>

      {/* Create manual token modal */}
      <Modal
        title="Create Manual API Token"
        open={createOpen}
        onCancel={() => { setCreateOpen(false); form.resetFields(); }}
        footer={null}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleCreateManual} style={{ marginTop: 8 }}>
          <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Token name is required' }]}>
            <Input placeholder="e.g. cursor-agent" />
          </Form.Item>
          <Form.Item
            name="scopes"
            label="Scopes"
            initialValue={['mcp:read']}
            rules={[{ required: true, message: 'Select at least one scope' }]}
          >
            <Select
              mode="multiple"
              options={SCOPES.map(s => ({ value: s, label: s }))}
              placeholder="Select scopes"
            />
          </Form.Item>
          <Form.Item name="expires_in_days" label="Expires in days (leave blank for no expiry)">
            <InputNumber min={1} max={3650} placeholder="e.g. 365" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item style={{ textAlign: 'right', marginBottom: 0 }}>
            <Space>
              <Button onClick={() => { setCreateOpen(false); form.resetFields(); }}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={creating}>Create Token</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default McpTokensPage;
